// File: src/pages/DashboardSponsorships.js

import React, { useEffect, useState } from 'react';
import { fetchSubscriptions } from '../api/subscriptions';
import { approveSponsorship, rejectSponsorship } from '../api/sponsorshipActions';
import '../assets/css/DashboardSponsorPosts.css'; // Custom styles

const DashboardSponsorships = () => {
  const [subs, setSubs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 5, current_page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  // Fetch subscriptions for a page
  const loadPage = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchSubscriptions(page, pagination.per_page);
      setSubs(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  loadPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending': return 'status-btn pending';
      case 'approved': return 'status-btn approved';
      case 'rejected': return 'status-btn rejected';
      default: return 'status-btn';
    }
  };

  // Handle approve action
  const handleApprove = async (sub) => {
    try {
      await approveSponsorship(sub.sponsored_post_id);
      setSubs(prev => prev.map(s =>
        s.sponsored_post_id === sub.sponsored_post_id
          ? { ...s, sponsor_status: 'approved' }
          : s
      ));
      alert('Sponsorship approved.');
    } catch (err) {
      alert(err.message || 'Failed to approve sponsorship.');
    }
  };

  // Handle reject action
  const handleReject = async (sub) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await rejectSponsorship(sub.sponsored_post_id, reason);
      setSubs(prev => prev.map(s =>
        s.sponsored_post_id === sub.sponsored_post_id
          ? { ...s, sponsor_status: 'rejected' }
          : s
      ));
      alert('Sponsorship rejected.');
    } catch (err) {
      alert(err.message || 'Failed to reject sponsorship.');
    }
  };

  return (
    <div className="dashboard-posts">
      <div className="posts-header">
        <h2>{pagination.total} Sponsored Posts</h2>
        <p>Quick overview of sponsorship activity.</p>
      </div>

      {loading && <p className="status-msg">Loading sponsorships…</p>}
      {error && <p className="status-msg error">{error}</p>}

      {!loading && !error && (
        <>
          <div className="posts-grid">
            {subs.map(sub => (
              <div className="post-card" key={sub.sponsored_post_id}>
                <div className="post-card-header">
                  <div className="media-sticker">
                    <span className="media-icon">🎬</span>
                  </div>
                  <div className="post-summary">
                    <p><strong>Post ID:</strong> {sub.post_id}</p>
                    <p><strong>Sponsor:</strong> {sub.sponsor_name}</p>
                    <p><strong>Amount:</strong> {sub.payment_amount}</p>
                    <span className={getStatusStyle(sub.sponsor_status)}>
                      {sub.sponsor_status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => toggleExpand(sub.sponsored_post_id)}
                  className="view-more-btn"
                >
                  {expanded[sub.sponsored_post_id] ? 'Hide Details' : 'View More'}
                </button>

                {expanded[sub.sponsored_post_id] && (
                  <div className="expanded-details">
                    <p><strong>Owner:</strong> {sub.post_owner_name}</p>
                    <p><strong>Status:</strong> {sub.payment_status} (Tracking: {sub.payment_tracking})</p>
                    <p><strong>Metric:</strong> {sub.metric_type} to {sub.metric_target}</p>
                    <p><strong>Website:</strong> {sub.website}</p>
                    <p><strong>Location:</strong> {sub.latitude}, {sub.longitude}</p>
                    <p><strong>Created:</strong> {sub.created_at}</p>
                    <p><strong>Expires:</strong> {sub.expired_date}</p>
                    <p><strong>Post Preview:</strong> {sub.post_content || '(No content)'}</p>

                   <div className="sponsor-actions">
                        <button 
                          onClick={() => handleApprove(sub)} 
                          className="approve-btn"
                        >
                          ✅ Approve
                        </button>
                        <button 
                          onClick={() => handleReject(sub)} 
                          className="reject-btn"
                        >
                          ❌ Reject
                        </button>
                      </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pagination">
            <button
              onClick={() => loadPage(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => loadPage(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.total_pages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardSponsorships;